<template>
  <div>
    <div :class="['number-input-container', alignClass]">
      <span v-if="unit && unitPosition === 'left'" class="unit">{{ unit }}</span>
      <input
        type="text"
        :min="min"
        :max="max"
        :step="step"
        :readonly="readonly"
        :placeholder="placeholder"
        v-model="displayValue"
        @input="handleInput"
        @blur="handleBlur"
        @keydown="saveCursorPosition"
        @keyup="restoreCursorPosition"
        :class="['number-input', alignClass]"
        :style="inputStyle"
      />
      <span v-if="unit && unitPosition === 'right'" class="unit">{{ unit }}</span>
      <div v-if="showControls" class="number-control-container">
        <button class="btn-up" @click="increaseValue">
          <feather-icon icon="ChevronUpIcon" svgClasses="h-3 w-3" />
        </button>
        <button class="btn-down" @click="decreaseValue">
          <feather-icon icon="ChevronDownIcon" svgClasses="h-3 w-3" />
        </button>
      </div>
    </div>
    <div class="printing">
      <div :class="['number-input-container', alignClass, 'print-container']">
        <span v-if="unit && unitPosition === 'left'" class="unit">{{ unit }}</span>
        <span class="number-input">{{ displayValue }}</span>
        <span v-if="unit && unitPosition === 'right'" class="unit">{{ unit }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "NumberInput",
  props: {
    value: {
      type: [Number, String],
      default: 0,
    },
    min: {
      type: Number,
      default: -Infinity,
    },
    max: {
      type: Number,
      default: Infinity,
    },
    step: {
      type: Number,
      default: 1,
    },
    unit: {
      type: String,
      default: "",
    },
    unitPosition: {
      type: String,
      default: "right", // 'left' 또는 'right'
    },
    align: {
      type: String,
      default: "right", // 'left', 'center', 'right'
    },
    showControls: {
      type: Boolean,
      default: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
    inputStyle: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      internalValue: this.value,
      // 내부에서 사용자 입력을 그대로 유지할 문자열 변수
      inputValue: this.formatNumber(this.value),
      cursorPosition: null,
      isArrowKey: false,
      isDeleteKey: false,
      lengthAtSaveCursorPosition: 0,
      isCtrlAll: false,
    };
  },
  computed: {
    displayValue: {
      get() {
        return this.inputValue;
      },
      set(val) {
        this.inputValue = val;
      },
    },
    alignClass() {
      return `text-${this.align}`;
    },
  },
  methods: {
    saveCursorPosition(event) {
      this.isCtrlAll = event.ctrlKey && event.key === "a";
      const arrowKeys = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];
      this.isArrowKey = arrowKeys.includes(event.key);
      this.isDeleteKey = event.key === "Delete";
      if (!this.isArrowKey && !this.isDeleteKey) {
        this.cursorPosition = event.target.selectionStart;
        this.lengthAtSaveCursorPosition = event.target.value.length;
      } else if (this.isDeleteKey) {
        this.cursorPosition = event.target.selectionStart;
      }
      // 첫 입력시 "0"을 지우고 바로 입력할 수 있도록
      if (!this.isArrowKey && this.cursorPosition !== null && !this.isCtrlAll && event.target.value === "0" && event.key.match(/[0-9]/)) {
        event.target.value = "";
        this.cursorPosition = 1;
      }
    },
    restoreCursorPosition(event) {
      if (!this.isArrowKey && this.cursorPosition !== null && !this.isCtrlAll) {
        const input = event.target;
        window.requestAnimationFrame(() => {
          const currentLength = input.value.length;
          const diff = currentLength - this.lengthAtSaveCursorPosition;
          let newCursorPosition = this.cursorPosition + diff;
          if (this.isDeleteKey) {
            newCursorPosition = this.cursorPosition;
          }
          input.setSelectionRange(newCursorPosition, newCursorPosition);
        });
      }
    },
    handleInput(event) {
      // 입력값에서 숫자, 소수점, 콤마, 마이너스만 허용
      const raw = event.target.value;
      const cleaned = raw.replace(/[^0-9.,-]/g, "");
      // 포맷팅 없이 입력값 그대로 저장 (중간 입력상태 보존)
      this.inputValue = cleaned;

      // 입력값이 "."으로 끝나지 않으면 숫자 변환 시도
      if (!cleaned.endsWith(".")) {
        const numericValue = Number(cleaned.replace(/,/g, ""));
        if (!isNaN(numericValue)) {
          this.internalValue = numericValue;
          this.$emit("input", numericValue);
        }
      }
    },
    handleBlur(event) {
      const raw = this.inputValue;
      const cleaned = raw.replace(/,/g, "");
      let value;
      if (cleaned.indexOf(".") !== -1) {
        value = parseFloat(cleaned);
      } else {
        value = parseInt(cleaned, 10);
      }
      if (isNaN(value)) {
        value = this.min;
      }
      if (value < this.min) {
        value = this.min;
      }
      if (value > this.max) {
        value = this.max;
      }
      if (this.internalValue !== value) {
        this.internalValue = value;
        this.$emit("input", value);
        // blur 시 최종 숫자 포맷 적용
        this.inputValue = this.formatNumber(value);
      }
    },
    formatNumber(value) {
      // 소수점 이하가 있을 경우 정수 부분만 천 단위 콤마 적용
      const parts = value.toString().split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return parts.join(".");
    },
    increaseValue() {
      if (this.readonly) return;
      let newValue = this.internalValue + this.step;
      if (newValue <= this.max) {
        if (newValue % 1 === 0) {
          newValue = parseInt(newValue);
        }
        this.internalValue = newValue;
        this.$emit("input", newValue);
        this.inputValue = this.formatNumber(newValue);
      }
    },
    decreaseValue() {
      if (this.readonly) return;
      let newValue = this.internalValue - this.step;
      if (newValue >= this.min) {
        if (newValue % 1 === 0) {
          newValue = parseInt(newValue);
        }
        this.internalValue = newValue;
        this.$emit("input", newValue);
        this.inputValue = this.formatNumber(newValue);
      }
    },
  },
  watch: {
    value(val) {
      this.internalValue = val;
      this.inputValue = this.formatNumber(val);
    },
  },
};
</script>

<style scoped>
.number-input-container {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
}

.unit {
  margin: 0 4px;
}

.number-input {
  display: block;
  width: 100%;
  text-align: right;
  padding: 10px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  font-size: 1rem;
  color: rgb(60, 60, 60);
  transition: padding-right 0.1s;
}

.number-input-container:hover .number-input,
.number-input-container:hover .number-input:hover {
  padding-right: 26px;
}

.number-input::-webkit-inner-spin-button,
.number-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.number-input[type="number"] {
  -moz-appearance: textfield;
}

.number-control-container {
  position: absolute;
  right: 4px;
  top: 4px;
  bottom: 4px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 2px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.1s;
}

.number-input-container:hover .number-control-container {
  opacity: 1;
  pointer-events: auto;
}

.number-control-container button {
  background: rgba(0, 0, 0, 0.1);
  border: none;
  padding: 0 4px;
  cursor: pointer;
  font-size: 1rem;
  line-height: 0;
  flex: 1;
  border-radius: 3px;
}

.btn-up:hover,
.btn-down:hover {
  background: rgba(0, 0, 0, 0.2);
}

.printing {
  display: none;
}

@media print {
  .printing {
    display: block;
  }
  .number-input-container {
    display: none;
  }
  .print-container {
    display: flex;
    align-items: center;
    width: 100%;
    position: relative;
  }
  .print-container .unit {
    margin: 0 4px;
  }
  .print-container .number-input {
    display: block;
    width: 100%;
    padding: 8px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 5px;
    font-size: 1rem;
    color: rgb(60, 60, 60);
  }
  .text-center .number-input {
    text-align: center;
  }
  .text-right .number-input {
    text-align: right;
  }
  .text-left .number-input {
    text-align: left;
  }
}
</style>
